import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GuardianRequest } from '@teentack/shared';
import {
  approveGuardianRequest,
  fetchGuardianRequests,
  rejectGuardianRequest,
} from '@/lib/api-client';

export const GUARDIAN_REQUESTS_KEY = ['guardian-requests'] as const;

/**
 * Teens waiting on the signed-in parent.
 *
 * Kept short-lived: a teen can name their guardian at any moment, and the
 * parent should see it without a hard reload.
 */
export function useGuardianRequests(enabled = true) {
  return useQuery<GuardianRequest[]>({
    queryKey: GUARDIAN_REQUESTS_KEY,
    queryFn: fetchGuardianRequests,
    enabled,
    staleTime: 15_000,
  });
}

export function useGuardianDecision() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { relationshipId: string; decision: 'approve' | 'reject' }>({
    mutationFn: ({ relationshipId, decision }) =>
      decision === 'approve'
        ? approveGuardianRequest(relationshipId)
        : rejectGuardianRequest(relationshipId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: GUARDIAN_REQUESTS_KEY });
    },
  });
}
